import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { UiProvider } from './context/UiContext';
import AdminLayout from './components/AdminLayout';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ArticlesList from './pages/articles/ArticlesList';
import ArticleForm from './pages/articles/ArticleForm';
import EducationalList from './pages/educational/EducationalList';
import EducationalForm from './pages/educational/EducationalForm';
import DisertationsList from './pages/disertations/DisertationsList';
import DisertationForm from './pages/disertations/DisertationForm';
import JournalsList from './pages/journals/JournalsList';
import ReleasesList from './pages/journals/ReleasesList';
import ReleaseForm from './pages/journals/ReleaseForm';
import DictionaryPage from './pages/dictionaries/DictionaryPage';

/**
 * Маршруты админки.
 *
 * Старые адреса (/admin/allarticles, /admin/addarticle, /admin/editarticle/:id и т.д.)
 * сохранены как редиректы: закладки и ссылки в переписке продолжают работать.
 */

const LEGACY_REDIRECTS = [
    ['/admin/allarticles', '/admin/articles'],
    ['/admin/addarticle', '/admin/articles/new'],
    ['/admin/editarticle/:id', '/admin/articles/:id'],
    ['/admin/addauthors', '/admin/dictionaries/authors'],
    ['/admin/addkeywords', '/admin/dictionaries/keywords'],

    ['/admin/alljournals', '/admin/journals'],
    ['/admin/allreleases', '/admin/releases'],
    ['/admin/addreleases', '/admin/releases/new'],
    ['/admin/editreleases/:id', '/admin/releases/:id'],

    ['/admin/alleducation', '/admin/educational'],
    ['/admin/addeducation', '/admin/educational/new'],
    ['/admin/editeducational/:id', '/admin/educational/:id'],
    ['/admin/addauthoreducation', '/admin/dictionaries/ed-authors'],
    ['/admin/addcategorieseducation', '/admin/dictionaries/ed-categories'],

    ['/admin/alldisertation', '/admin/disertations'],
    ['/admin/adddisertation', '/admin/disertations/new'],
    ['/admin/editdisertation/:id', '/admin/disertations/:id'],
    ['/admin/addauthordisertation', '/admin/dictionaries/dis-authors'],
    ['/admin/addnapravleniedisertation', '/admin/dictionaries/dis-napravlenie'],
    ['/admin/addspecdisertation', '/admin/dictionaries/dis-spec'],
];

/**
 * Пускает дальше только авторизованных. В отличие от прежнего Protected,
 * который делал редирект в useEffect уже после отрисовки — из-за чего защищённая
 * страница на мгновение показывалась незалогиненному пользователю.
 */
const RequireAuth = ({ children }) => {
    const { isAuthenticated, checking } = useAuth();
    const location = useLocation();

    if (checking) {
        return (
            <div className="adm-root" style={{ padding: 60, textAlign: 'center', color: '#6b7684' }}>
                Проверка сессии…
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Redirect to={{ pathname: '/admin/login', state: { from: location.pathname } }} />;
    }

    return children;
};

const AdminRoutes = () => (
    <AuthProvider>
        <UiProvider>
            <Switch>
                <Route path="/admin/login" exact component={Login} />

                {LEGACY_REDIRECTS.map(([from, to]) => (
                    <Redirect key={from} exact from={from} to={to} />
                ))}

                <Route
                    path="/admin"
                    render={() => (
                        <RequireAuth>
                            <AdminLayout>
                                <Switch>
                                    <Route path="/admin" exact component={Dashboard} />

                                    <Route path="/admin/articles" exact component={ArticlesList} />
                                    <Route path="/admin/articles/new" exact component={ArticleForm} />
                                    <Route path="/admin/articles/:id" exact component={ArticleForm} />

                                    <Route path="/admin/journals" exact component={JournalsList} />

                                    <Route path="/admin/releases" exact component={ReleasesList} />
                                    <Route path="/admin/releases/new" exact component={ReleaseForm} />
                                    <Route path="/admin/releases/:id" exact component={ReleaseForm} />

                                    <Route path="/admin/educational" exact component={EducationalList} />
                                    <Route path="/admin/educational/new" exact component={EducationalForm} />
                                    <Route path="/admin/educational/:id" exact component={EducationalForm} />

                                    <Route path="/admin/disertations" exact component={DisertationsList} />
                                    <Route path="/admin/disertations/new" exact component={DisertationForm} />
                                    <Route path="/admin/disertations/:id" exact component={DisertationForm} />

                                    {/* key по типу справочника: при переходе между справочниками
                                        маршрут тот же, поэтому без него React переиспользовал бы
                                        компонент, и на экране оставались данные, поиск, страница
                                        и фильтр от предыдущего справочника. */}
                                    <Route
                                        path="/admin/dictionaries/:type"
                                        exact
                                        render={({ match }) => (
                                            <DictionaryPage key={match.params.type} />
                                        )}
                                    />

                                    <Route
                                        render={() => (
                                            <div className="adm-card adm-card-body">
                                                <h1 className="adm-page-title">Страница не найдена</h1>
                                                <p className="adm-muted">
                                                    Выберите раздел в меню слева.
                                                </p>
                                            </div>
                                        )}
                                    />
                                </Switch>
                            </AdminLayout>
                        </RequireAuth>
                    )}
                />
            </Switch>
        </UiProvider>
    </AuthProvider>
);

export default AdminRoutes;
